import { X } from "lucide-react";
import { useState, useCallback, useRef } from "react";

const fmt = (v) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(v || 0);

const pct = (v) => `${(parseFloat(v) || 0).toFixed(1)}%`;

// ─── FORMULA DEFINITIONS (unchanged, omitted for brevity) ─────────────
const FORMULAS = {
  soldAsIsValue: {
    label: "Sold AS-IS Value",
    f: "Avg Sold Price/SqFt × Subject SqFt",
    vars: (s, c) => [
      ["Avg Sold $/SqFt", `$${c.avgSoldPsf.toFixed(2)}`],
      ["Subject SqFt", s.sqft],
      ["Result", fmt(c.soldAsIsValue)],
    ],
  },
  activeAsIsValue: {
    label: "Active AS-IS Value",
    f: "Avg List Price/SqFt × Subject SqFt",
    vars: (s, c) => [
      ["Avg List $/SqFt", `$${c.avgActivePsf.toFixed(2)}`],
      ["Subject SqFt", s.sqft],
      ["Result", fmt(c.activeAsIsValue)],
    ],
  },
  combinedValue: {
    label: "Combined Value",
    f: "(Sold AS-IS Value + Active AS-IS Value) ÷ 2",
    vars: (s, c) => [
      ["Sold AS-IS", fmt(c.soldAsIsValue)],
      ["Active AS-IS", fmt(c.activeAsIsValue)],
      ["Result", fmt(c.combinedValue)],
    ],
  },
  adjustedAsIs: {
    label: "Adjusted AS-IS Value",
    f: "Combined Value × (1 − Market Adjustment %)",
    vars: (s, c) => [
      ["Combined Value", fmt(c.combinedValue)],
      ["Market Adj.", pct(s.marketAdj)],
      ["Result", fmt(c.adjustedAsIs)],
    ],
  },
  closingFeesAmt: {
    label: "Closing Fees Amount",
    f: "Adjusted AS-IS Value × Closing Fees %",
    vars: (s, c) => [
      ["Adjusted AS-IS", fmt(c.adjustedAsIs)],
      ["Closing Fees %", pct(s.closingFeesPct)],
      ["Result", fmt(c.closingFeesAmt)],
    ],
  },
  mao: {
    label: "Max Allowable Offer (MAO)",
    f: "Adjusted AS-IS Value − Closing Fees Amount − Desired Profit",
    vars: (s, c) => [
      ["Adjusted AS-IS", fmt(c.adjustedAsIs)],
      ["Closing Fees", fmt(c.closingFeesAmt)],
      ["Desired Profit", fmt(s.desiredProfit)],
      ["Result", fmt(c.mao)],
    ],
  },
  avgSoldPrice: {
    label: "Avg Sold Price",
    f: "Sum of Sold Prices ÷ Number of Sold Comps",
    vars: (s, c) => {
      const valid = s.solds.filter((x) => x.sqft && x.price);
      return [...valid.map((x, i) => [`Comp ${i + 1} Price`, fmt(x.price)]), ["Count", valid.length], ["Result", fmt(c.avgSoldPrice)]];
    },
  },
  avgSoldSqft: {
    label: "Avg Sold SqFt",
    f: "Sum of Sold SqFt ÷ Number of Sold Comps",
    vars: (s, c) => {
      const valid = s.solds.filter((x) => x.sqft && x.price);
      return [...valid.map((x, i) => [`Comp ${i + 1} SqFt`, x.sqft.toLocaleString()]), ["Count", valid.length], ["Result", Math.round(c.avgSoldSqft).toLocaleString()]];
    },
  },
  avgSoldPsf: {
    label: "Avg Sold Price/SqFt",
    f: "Average of (Sold Price ÷ SqFt) for each comp",
    vars: (s, c) => [...s.solds.filter((x) => x.sqft && x.price).map((x, i) => [`Comp ${i + 1}`, `$${(x.price / x.sqft).toFixed(2)}/sqft`]), ["Average", `$${c.avgSoldPsf.toFixed(2)}/sqft`]],
  },
  avgActivePrice: {
    label: "Avg List Price",
    f: "Sum of List Prices ÷ Number of Active Comps",
    vars: (s, c) => {
      const valid = s.actives.filter((x) => x.sqft && x.price);
      return [...valid.map((x, i) => [`Comp ${i + 1} Price`, fmt(x.price)]), ["Count", valid.length], ["Result", fmt(c.avgActivePrice)]];
    },
  },
  avgActiveSqft: {
    label: "Avg Active SqFt",
    f: "Sum of Active SqFt ÷ Number of Active Comps",
    vars: (s, c) => {
      const valid = s.actives.filter((x) => x.sqft && x.price);
      return [...valid.map((x, i) => [`Comp ${i + 1} SqFt`, x.sqft.toLocaleString()]), ["Count", valid.length], ["Result", Math.round(c.avgActiveSqft).toLocaleString()]];
    },
  },
  avgActivePsf: {
    label: "Avg List Price/SqFt",
    f: "Average of (List Price ÷ SqFt) for each active comp",
    vars: (s, c) => [...s.actives.filter((x) => x.sqft && x.price).map((x, i) => [`Comp ${i + 1}`, `$${(x.price / x.sqft).toFixed(2)}/sqft`]), ["Average", `$${c.avgActivePsf.toFixed(2)}/sqft`]],
  },
};

// ─── SUB‑COMPONENTS (Tailwind) ────────────────────────────────────────
function FormulaCell({ id, value, computed, inputs }) {
  const [show, setShow] = useState(false);
  const timerRef = useRef(null);
  const def = FORMULAS[id];
  if (!def) return <span className="text-sm font-medium text-gray-900">{value}</span>;

  const vars = def.vars(inputs, computed);

  const handleMouseEnter = () => {
    timerRef.current = setTimeout(() => setShow(true), 300);
  };
  const handleMouseLeave = () => {
    clearTimeout(timerRef.current);
    setShow(false);
  };

  return (
    <div className="relative inline-block">
      <button onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 text-sm font-medium text-blue-700 flex items-center gap-1 hover:bg-blue-100 transition-colors" type="button">
        {value}
      </button>
      {show && (
        <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="absolute z-50 top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-3 min-w-[240px] text-left">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-semibold text-gray-800">{def.label}</span>
          </div>
          <div className="bg-gray-50 rounded-md p-2 text-[11px] font-mono text-gray-600 border border-gray-100 mb-2">{def.f}</div>
          <div className="flex flex-col gap-1">
            {vars.map(([k, v]) => (
              <div key={k} className="flex justify-between text-[11px]">
                <span className="text-gray-500">{k}</span>
                <span className={k === "Result" || k === "Average" ? "font-medium text-blue-700" : "text-gray-800"}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title, color }) {
  return (
    <div className="text-white text-[11px] font-medium tracking-wide uppercase rounded-md px-2.5 py-1 mt-3 mb-1.5" style={{ backgroundColor: color }}>
      {title}
    </div>
  );
}

function ResultRow({ label, children, bold }) {
  return (
    <div className={`flex items-center justify-between py-1 border-b border-gray-100 ${bold ? "font-medium" : ""}`}>
      <span className={`text-xs ${bold ? "text-gray-900" : "text-gray-600"}`}>{label}</span>
      {children}
    </div>
  );
}

function CompRow({ num, comp, onChange, onRemove, type, showDays = false }) {
  const psf = comp.sqft && comp.price ? (comp.price / comp.sqft).toFixed(0) : "—";

  let daysSince = "—";
  if (showDays && comp.date) {
    const saleDate = new Date(comp.date + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (!isNaN(saleDate.getTime())) {
      const diffTime = today - saleDate;
      daysSince = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      if (daysSince < 0) daysSince = "—";
    }
  }

  return (
    <div className={`grid items-center py-0.5 border-b border-gray-100 text-[10px] ${showDays ? "grid-cols-[18px_minmax(0,2fr)_60px_72px_60px_120px_48px_24px]" : "grid-cols-[18px_minmax(0,2fr)_60px_72px_60px_64px_24px]"} gap-1`}>
      <span className="text-gray-400 font-semibold">{num}</span>
      <input type="text" placeholder="Address" value={comp.address} onChange={(e) => onChange({ ...comp, address: e.target.value })} className="w-full text-[10px] px-1 py-0.5 border border-gray-200 rounded" />
      <input type="number" placeholder="SqFt" value={comp.sqft} onChange={(e) => onChange({ ...comp, sqft: parseFloat(e.target.value) || 0 })} className="w-full text-[10px] px-1 py-0.5 border border-gray-200 rounded text-right" />
      <input type="number" placeholder={type === "sold" ? "Sold Price" : "List Price"} value={comp.price} onChange={(e) => onChange({ ...comp, price: parseFloat(e.target.value) || 0 })} className="w-full text-[10px] px-1 py-0.5 border border-gray-200 rounded text-right" />
      <span className="text-gray-500 text-right">{psf !== "—" ? `$${psf}` : "—"}</span>
      <input type="date" value={comp.date} onChange={(e) => onChange({ ...comp, date: e.target.value })} className="w-full text-[10px] px-1 py-0.5 border border-gray-200 rounded" />
      {showDays && <span className="text-gray-500 text-right text-[10px]">{daysSince !== "—" ? daysSince : "—"}</span>}
      <button onClick={onRemove} className="text-red-500 hover:text-red-700 text-lg leading-none p-0 flex items-center justify-center" title="Remove comp">
        <X size={12} />
      </button>
    </div>
  );
}

const defaultComp = () => ({ address: "", sqft: 0, price: 0, date: "" });

export default function NovationInstantOfferCalculator() {
  const [s, setS] = useState({
    address: "123 main street",
    sqft: 1633,
    closingFeesPct: 10,
    desiredProfit: 40000,
    marketAdj: 10,
    solds: [{ address: "", sqft: 1500, price: 220000, date: "2024-03-01" }, { address: "", sqft: 1400, price: 200000, date: "2024-03-01" }, { address: "", sqft: 1700, price: 210000, date: "2024-03-01" }, { address: "", sqft: 1650, price: 200000, date: "2024-03-01" }, defaultComp()],
    actives: [{ address: "", sqft: 1500, price: 220000, date: "2024-03-01" }, { address: "", sqft: 1400, price: 215000, date: "2024-03-01" }, { address: "", sqft: 1700, price: 225000, date: "2024-03-01" }, { address: "", sqft: 1650, price: 215000, date: "2024-03-01" }, defaultComp()],
  });

  const set = useCallback((k) => (v) => setS((p) => ({ ...p, [k]: v })), []);

  // Add / Remove helpers
  const addSold = () => setS((p) => ({ ...p, solds: [...p.solds, defaultComp()] }));
  const removeSold = (i) => setS((p) => ({ ...p, solds: p.solds.filter((_, idx) => idx !== i) }));
  const addActive = () => setS((p) => ({ ...p, actives: [...p.actives, defaultComp()] }));
  const removeActive = (i) => setS((p) => ({ ...p, actives: p.actives.filter((_, idx) => idx !== i) }));

  // derived values
  const soldComps = s.solds.filter((x) => x.sqft && x.price);
  const activeComps = s.actives.filter((x) => x.sqft && x.price);

  const avgSoldPsf = soldComps.length ? soldComps.reduce((a, x) => a + x.price / x.sqft, 0) / soldComps.length : 0;
  const avgActivePsf = activeComps.length ? activeComps.reduce((a, x) => a + x.price / x.sqft, 0) / activeComps.length : 0;

  const soldAsIsValue = avgSoldPsf * s.sqft;
  const activeAsIsValue = avgActivePsf * s.sqft;
  const combinedValue = (soldAsIsValue + activeAsIsValue) / 2;
  const adjustedAsIs = combinedValue * (1 - s.marketAdj / 100);
  const closingFeesAmt = (adjustedAsIs * s.closingFeesPct) / 100;
  const mao = adjustedAsIs - closingFeesAmt - s.desiredProfit;

  const avgSoldSqft = soldComps.length ? soldComps.reduce((a, x) => a + x.sqft, 0) / soldComps.length : 0;
  const avgSoldPrice = soldComps.length ? soldComps.reduce((a, x) => a + x.price, 0) / soldComps.length : 0;
  const avgActiveSqft = activeComps.length ? activeComps.reduce((a, x) => a + x.sqft, 0) / activeComps.length : 0;
  const avgActivePrice = activeComps.length ? activeComps.reduce((a, x) => a + x.price, 0) / activeComps.length : 0;

  const c = {
    avgSoldPrice,
    avgSoldSqft,
    avgSoldPsf,
    avgActivePrice,
    avgActiveSqft,
    avgActivePsf,
    soldAsIsValue,
    activeAsIsValue,
    combinedValue,
    adjustedAsIs,
    closingFeesAmt,
    mao,
  };

  return (
    <div className="font-sans max-w-4xl mx-auto px-4 pb-8">
      {/* Header */}
      <div className="py-3 px-4 border-b border-gray-200 mb-2">
        <div className="text-base font-semibold text-gray-900 mb-1">Novation Instant Offer Calculator</div>
        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-2">
          <input type="text" value={s.address} onChange={(e) => set("address")(e.target.value)} placeholder="Property address" className="w-full text-xs px-2 py-1 border border-gray-200 rounded-md text-gray-600" />
          <div className="flex items-center gap-1">
            <label className="text-xs text-gray-500 whitespace-nowrap">SqFt</label>
            <input type="number" value={s.sqft} onChange={(e) => set("sqft")(parseFloat(e.target.value) || 0)} className="w-full text-xs px-2 py-1 border border-gray-200 rounded-md text-right" />
          </div>
        </div>
      </div>

      <div className="px-4">
        {/* Novation Buy Formula */}
        <SectionHeader title="Novation Buy Formula" color="#185FA5" />
        <ResultRow label="Adjusted AS-IS Value">
          <FormulaCell id="adjustedAsIs" value={fmt(adjustedAsIs)} computed={c} inputs={s} />
        </ResultRow>

        <div className="flex items-center justify-between py-1 border-b border-gray-100">
          <label className="text-xs text-gray-600">Closing Fees %</label>
          <div className="flex items-center gap-2">
            <input type="number" value={s.closingFeesPct} step={0.5} min={0} max={100} onChange={(e) => set("closingFeesPct")(parseFloat(e.target.value) || 0)} className="w-14 text-xs text-right px-1 py-0.5 border border-gray-200 rounded" />
            <span className="text-xs text-gray-400">%</span>
            <FormulaCell id="closingFeesAmt" value={fmt(closingFeesAmt)} computed={c} inputs={s} />
          </div>
        </div>

        <div className="flex items-center justify-between py-1 border-b border-gray-100">
          <label className="text-xs text-gray-600">Desired Profit</label>
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-400">$</span>
            <input type="number" value={s.desiredProfit} onChange={(e) => set("desiredProfit")(parseFloat(e.target.value) || 0)} className="w-24 text-xs text-right px-2 py-1 border border-gray-200 rounded" />
          </div>
        </div>

        <ResultRow label="MAX ALLOWABLE OFFER (MAO)" bold>
          <FormulaCell id="mao" value={fmt(mao)} computed={c} inputs={s} />
        </ResultRow>

        {/* SOLDS */}
        <SectionHeader title="AS-IS Comps — Solds" color="#3B6D11" />
        <div className="grid grid-cols-[18px_minmax(0,2fr)_60px_72px_60px_120px_48px_24px] gap-1 py-1 border-b border-gray-200 text-[9px] font-semibold text-gray-400 uppercase">
          {["#", "Address", "SqFt", "Sold Price", "$/SqFt", "Date", "Days", ""].map((h) => (
            <span key={h}>{h}</span>
          ))}
        </div>
        {s.solds.map((comp, i) => (
          <CompRow
            key={i}
            num={i + 1}
            comp={comp}
            type="sold"
            showDays={true}
            onChange={(v) =>
              setS((p) => ({
                ...p,
                solds: p.solds.map((c, j) => (j === i ? v : c)),
              }))
            }
            onRemove={() => removeSold(i)}
          />
        ))}
        {/* Add comp button */}
        <div className="py-1">
          <button onClick={addSold} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
            <span className="text-lg leading-none">+</span> Add Comp
          </button>
        </div>

        {/* Avg row for solds */}
        <div className="grid grid-cols-[18px_minmax(0,2fr)_60px_72px_60px_120px_48px_24px] gap-1 py-1 items-center bg-gray-50 rounded-md mt-1 text-[10px]">
          <span className="font-semibold text-gray-600 text-center">Avg</span>
          <span></span>
          <div className="text-right">
            <FormulaCell id="avgSoldSqft" value={avgSoldSqft > 0 ? Math.round(avgSoldSqft).toLocaleString() : "—"} computed={c} inputs={s} />
          </div>
          <div className="text-right">
            <FormulaCell id="avgSoldPrice" value={avgSoldPrice > 0 ? fmt(avgSoldPrice) : "—"} computed={c} inputs={s} />
          </div>
          <div className="text-right">
            <FormulaCell id="avgSoldPsf" value={avgSoldPsf > 0 ? `$${avgSoldPsf.toFixed(0)}` : "—"} computed={c} inputs={s} />
          </div>
          <span></span>
          <span></span>
          <span></span>
        </div>
        <ResultRow label="Sold AS-IS Value">
          <FormulaCell id="soldAsIsValue" value={fmt(soldAsIsValue)} computed={c} inputs={s} />
        </ResultRow>

        {/* ACTIVES */}
        <SectionHeader title="AS-IS Comps — Actives" color="#854F0B" />
        <div className="grid grid-cols-[18px_minmax(0,2fr)_60px_72px_60px_120px_48px_24px] gap-1 py-1 border-b border-gray-200 text-[9px] font-semibold text-gray-400 uppercase">
          {["#", "Address", "SqFt", "List Price", "$/SqFt", "Date", "Days", ""].map((h) => (
            <span key={h}>{h}</span>
          ))}
        </div>
        {s.actives.map((comp, i) => (
          <CompRow
            key={i}
            num={i + 1}
            comp={comp}
            type="active"
            showDays={true}
            onChange={(v) =>
              setS((p) => ({
                ...p,
                actives: p.actives.map((c, j) => (j === i ? v : c)),
              }))
            }
            onRemove={() => removeActive(i)}
          />
        ))}
        <div className="py-1">
          <button onClick={addActive} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
            <span className="text-lg leading-none">+</span> Add Comp
          </button>
        </div>

        {/* Avg row for actives */}
        <div className="grid grid-cols-[18px_minmax(0,2fr)_60px_72px_60px_120px_48px_24px] gap-1 py-1 items-center bg-gray-50 rounded-md mt-1 text-[10px]">
          <span className="font-semibold text-gray-600 text-center">Avg</span>
          <span></span>
          <div className="text-right">
            <FormulaCell id="avgActiveSqft" value={avgActiveSqft > 0 ? Math.round(avgActiveSqft).toLocaleString() : "—"} computed={c} inputs={s} />
          </div>
          <div className="text-right">
            <FormulaCell id="avgActivePrice" value={avgActivePrice > 0 ? fmt(avgActivePrice) : "—"} computed={c} inputs={s} />
          </div>
          <div className="text-right">
            <FormulaCell id="avgActivePsf" value={avgActivePsf > 0 ? `$${avgActivePsf.toFixed(0)}` : "—"} computed={c} inputs={s} />
          </div>
          <span></span>
          <span></span>
          <span></span>
        </div>
        <ResultRow label="Active AS-IS Value">
          <FormulaCell id="activeAsIsValue" value={fmt(activeAsIsValue)} computed={c} inputs={s} />
        </ResultRow>

        {/* Market Adjustment */}
        <SectionHeader title="Market Adjustment (30-Day Sale)" color="#533e8e" />
        <div className="mt-2 bg-white border border-gray-200 rounded-md overflow-hidden">
          <div className="grid grid-cols-3 bg-gray-100 text-[11px] font-medium text-gray-600 uppercase tracking-wide">
            <div className="px-3 py-2 text-center">COMBINED VALUE</div>
            <div className="px-3 py-2 text-center">ADJUSTMENT</div>
            <div className="px-3 py-2 text-center">ADJUSTED AS-IS VALUE</div>
          </div>
          <div className="grid grid-cols-3 border-b border-gray-100 items-center">
            <div className="p-4">
              <FormulaCell id="combinedValue" value={fmt(combinedValue)} computed={c} inputs={s} />
            </div>
            <div className="p-4 flex items-center justify-center gap-2">
              <input type="number" value={s.marketAdj} step={0.5} min={0} max={100} onChange={(e) => set("marketAdj")(parseFloat(e.target.value) || 0)} className="w-14 text-xs text-right px-1 py-0.5 border border-gray-200 rounded" />
              <span className="text-xs text-gray-400">%</span>
            </div>
            <div className="p-4">
              <FormulaCell id="adjustedAsIs" value={fmt(adjustedAsIs)} computed={c} inputs={s} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
